import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import ConfirmModal from './ConfirmModal';
import './DependencyManager.css';

/**
 * Dependency Manager component for viewing and managing task dependencies
 */
const DependencyManager = ({ 
  taskId, 
  projectId,
  canManage = false,
  onDependencyChange = () => {}
}) => {
  const [dependencies, setDependencies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedType, setSelectedType] = useState('blocked_by');
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Remove',
    variant: 'warning',
    onConfirm: null,
    isLoading: false
  });

  useEffect(() => {
    fetchDependencies();
  }, [taskId]);

  const fetchDependencies = async () => {
    try {
      const response = await api.get(`/tasks/${taskId}/dependencies`);
      setDependencies(response.data);
    } catch (error) {
      console.error('Failed to fetch dependencies:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchTasks = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await api.get(`/projects/${projectId}/tasks`, {
        params: { search: query, page_size: 10 }
      });
      // Filter out current task
      const filtered = response.data.tasks.filter(t => t.id !== taskId);
      setSearchResults(filtered);
    } catch (error) {
      console.error('Failed to search tasks:', error);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      searchTasks(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleAddDependency = async (dependencyTaskId) => {
    try {
      await api.post(`/tasks/${taskId}/dependencies`, {
        dependency_task_id: dependencyTaskId,
        dependency_type: selectedType
      });
      toast.success('Dependency added');
      setShowAddModal(false);
      setSearchQuery('');
      setSearchResults([]);
      fetchDependencies();
      onDependencyChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add dependency');
    }
  };

  const handleRemoveDependency = (dependencyId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Remove Dependency',
      message: 'Are you sure you want to remove this dependency?',
      confirmText: 'Remove',
      variant: 'warning',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await api.delete(`/dependencies/${dependencyId}`);
          toast.success('Dependency removed');
          fetchDependencies();
          onDependencyChange();
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to remove dependency');
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  const getTypeLabel = (type) => {
    const labels = {
      'blocked_by': 'Blocked by',
      'blocks': 'Blocks',
      'relates_to': 'Related to',
      'follow_up': 'Follow-up of'
    };
    return labels[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      'backlog': 'var(--neutral-500)',
      'in_progress': 'var(--primary-500)',
      'review': 'var(--warning-500)',
      'done': 'var(--success-500)'
    };
    return colors[status] || 'var(--neutral-500)';
  };

  if (loading) {
    return <div className="dependency-loading">Loading dependencies...</div>;
  }

  if (!dependencies) {
    return null;
  }

  const hasAnyDependencies = 
    dependencies.blocked_by.length > 0 || 
    dependencies.blocking.length > 0 || 
    dependencies.related.length > 0 ||
    dependencies.follow_ups.length > 0;

  return (
    <div className="dependency-manager">
      <div className="dependency-header">
        <h4>Dependencies</h4>
        {canManage && (
          <button 
            className="btn btn-sm btn-ghost"
            onClick={() => setShowAddModal(true)}
          >
            + Add
          </button>
        )}
      </div>

      {dependencies.is_blocked && (
        <div className="blocked-warning">
          <span className="warning-icon">⚠</span>
          This task is blocked
        </div>
      )}

      {!hasAnyDependencies && (
        <div className="no-dependencies">No dependencies</div>
      )}

      {dependencies.blocked_by.length > 0 && (
        <DependencySection
          title="Blocked by"
          items={dependencies.blocked_by}
          canManage={canManage}
          onRemove={handleRemoveDependency}
          getStatusColor={getStatusColor}
        />
      )}

      {dependencies.blocking.length > 0 && (
        <DependencySection
          title="Blocking"
          items={dependencies.blocking}
          canManage={canManage}
          onRemove={handleRemoveDependency}
          getStatusColor={getStatusColor}
        />
      )}

      {dependencies.related.length > 0 && (
        <DependencySection
          title="Related"
          items={dependencies.related}
          canManage={canManage}
          onRemove={handleRemoveDependency}
          getStatusColor={getStatusColor}
        />
      )}

      {dependencies.follow_ups.length > 0 && (
        <DependencySection
          title="Follow-ups"
          items={dependencies.follow_ups}
          canManage={canManage}
          onRemove={handleRemoveDependency}
          getStatusColor={getStatusColor}
        />
      )}

      {/* Add Dependency Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="add-dependency-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Dependency</h3>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Dependency Type</label>
                <select
                  className="form-input form-select"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  <option value="blocked_by">Blocked by</option>
                  <option value="relates_to">Related to</option>
                  <option value="follow_up">Follow-up of</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Search for task</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search by title or key..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
              
              <div className="search-results">
                {searching && <div className="searching">Searching...</div>}
                
                {!searching && searchResults.length === 0 && searchQuery.length >= 2 && (
                  <div className="no-results">No tasks found</div>
                )}
                
                {searchResults.map((task) => (
                  <div 
                    key={task.id} 
                    className="search-result-item"
                    onClick={() => handleAddDependency(task.id)}
                  >
                    <span className="task-key">{task.task_key}</span>
                    <span className="task-title">{task.title}</span>
                    <span 
                      className="task-status"
                      style={{ backgroundColor: getStatusColor(task.status) }}
                    >
                      {task.status.replace('_', ' ')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        isLoading={confirmModal.isLoading}
      />
    </div>
  );
};

/**
 * Section for a specific dependency type
 */
const DependencySection = ({ title, items, canManage, onRemove, getStatusColor }) => (
  <div className="dependency-section">
    <h5 className="section-title">{title}</h5>
    <div className="dependency-list">
      {items.map((dep) => (
        <div 
          key={dep.id} 
          className={`dependency-item ${dep.is_blocking ? 'blocking' : ''}`}
        >
          <div className="dep-info">
            <span 
              className="dep-status-dot"
              style={{ backgroundColor: getStatusColor(dep.task_status) }}
            />
            <span className="dep-key">{dep.task_key}</span>
            <span className="dep-title">{dep.task_title}</span>
          </div>
          {dep.is_blocking && (
            <span className="blocking-badge">Blocking</span>
          )}
          {canManage && (
            <button 
              className="remove-btn"
              onClick={() => onRemove(dep.id)}
              title="Remove dependency"
            >
              ×
            </button>
          )}
        </div>
      ))}
    </div>
  </div>
);

/**
 * Small blocked indicator for board cards
 */
export const BlockedIndicator = ({ isBlocked, blockerCount = 0 }) => {
  if (!isBlocked) return null;
  
  return (
    <span className="blocked-indicator" title={`Blocked by ${blockerCount} task(s)`}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
      </svg>
    </span>
  );
};

export default DependencyManager;
